import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation, Navigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API, useAuth } from "../../App";
import Header from "../../components/layout/Header";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  LayoutDashboard,
  Calendar,
  CalendarDays,
  Star,
  Building2,
  Image,
  Clock,
  CreditCard,
  Users,
  Settings,
  Eye,
  UtensilsCrossed,
  Megaphone,
  AlertTriangle,
  ClipboardList,
  Lock,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import OwnerCalendar from "./OwnerCalendar";
import OwnerAvailability from "./OwnerAvailability";
import OwnerScheduling from "./OwnerScheduling";
import OwnerMenu from "./OwnerMenu";
import OwnerPromotions from "./OwnerPromotions";
import OwnerGalleryPage from "./OwnerGallery";
import OverviewPage from "./OverviewPage";
import OwnerBookingsPage from "./OwnerBookingsPage";
import OwnerVenuePage from "./OwnerVenuePage";
import OwnerBillingPage from "./OwnerBillingPage";
import VenuePreviewPage from "./VenuePreviewPage";
import {
  OwnerReviewsPage,
  OwnerTeamPage,
  OwnerSettingsPage,
} from "./OwnerSimplePages";
import "leaflet/dist/leaflet.css";

export default function OwnerDashboard() {
  const location = useLocation();
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [venues, setVenues] = useState([]);
  const [activeVenueId, setActiveVenueId] = useState(
    typeof window !== "undefined" ? localStorage.getItem("active_venue_id") || "" : "",
  );

  useEffect(() => {
    axios
      .get(`${API}/owner/venues`)
      .then((res) => {
        const list = res.data || [];
        setVenues(list);
        if (list.length === 0) return;
        // Reconcile a stale `active_venue_id`: if the localStorage value
        // no longer matches a venue the user actually owns (e.g. ownership
        // was transferred or the venue was deleted), every /api/owner/*
        // request would silently 404 — the bell would still fire on the
        // notifications endpoint but every page would render empty.
        // Falling back to the first owned venue keeps the dashboard usable.
        const stillOwns = list.some((v) => v.venue_id === activeVenueId);
        if (!activeVenueId || !stillOwns) {
          const fallback = list[0].venue_id;
          setActiveVenueId(fallback);
          localStorage.setItem("active_venue_id", fallback);
          if (activeVenueId && !stillOwns) {
            // We just changed the active venue under the user's feet — do a
            // soft reload so every page re-fetches with the new venue_id
            // params. Without this, in-flight queries stay scoped to the
            // stale venue.
            window.location.reload();
          }
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleVenueChange = (id) => {
    localStorage.setItem("active_venue_id", id);
    setActiveVenueId(id);
    window.location.reload();
  };

  const activeVenue = venues.find((v) => v.venue_id === activeVenueId) || venues[0];
  const lang = i18n.language;
  const activeVenueName = activeVenue
    ? (lang === "el" && activeVenue.name_el) || activeVenue.name
    : "";

  const bookingsEnabled = activeVenue?.bookings_enabled !== false;
  const featureToggles = activeVenue?.feature_toggles || {};
  // Phase 3: nav items that need a plan toggle on. Toggle missing → assume on
  // (treats venues that haven't been migrated to the plan engine as fully
  // featured to avoid false "locked" states).
  const featureCan = (key) => featureToggles[key] !== false;
  const planName = (lang === "el" && activeVenue?.venue_plan_name_el) || activeVenue?.venue_plan_name || activeVenue?.venue_plan_key;

  // Full ordered nav. `requires` points to a plan feature toggle. When the
  // toggle is OFF the item is shown as locked instead of hidden so the owner
  // sees what's available on higher plans.
  const allNavItems = [
    { path: "/owner", label: t("owner:overview", { defaultValue: "Overview" }), icon: LayoutDashboard, bookingsGated: true, requires: "owner_dashboard" },
    { path: "/owner/venue", label: t("owner_venue:title"), icon: Building2 },
    { path: "/owner/gallery", label: t("owner:gallery"), icon: Image, requires: "photos_extended" },
    { path: "/owner/availability", label: t("owner:availability"), icon: Clock, requires: "availability_overrides" },
    { path: "/owner/menu", label: t("owner:menu"), icon: UtensilsCrossed, requires: "menu_module" },
    { path: "/owner/promotions", label: t("owner:offers", { defaultValue: "Offers" }), icon: Megaphone, requires: "news_posts" },
    { path: "/owner/bookings", label: t("owner:bookings"), icon: Calendar, bookingsGated: true, requires: "bookings_enabled" },
    { path: "/owner/calendar", label: t("owner:calendar"), icon: CalendarDays, bookingsGated: true, requires: "calendar_view" },
    { path: "/owner/reviews", label: t("owner:reviews"), icon: Star, bookingsGated: true, requires: "reviews_enabled" },
    { path: "/owner/schedule", label: t("owner:staff"), icon: ClipboardList, bookingsGated: true, requires: "table_layout" },
    { path: "/owner/preview", label: t("owner:preview", { defaultValue: "Preview" }), icon: Eye },
    { path: "/owner/billing", label: t("owner:billing"), icon: CreditCard },
    { path: "/owner/team", label: t("owner:team", { defaultValue: "Team" }), icon: Users, bookingsGated: true, requires: "table_layout" },
    { path: "/owner/settings", label: t("owner:settings"), icon: Settings },
  ];
  // Phase 4 (P3): the owner-dashboard sidebar is now driven by the resolved
  // layout for the active venue (per-venue override > plan default > builtin).
  // Locked items are hidden entirely (per user spec); admins control visibility
  // via the layout builder at /admin/layouts.
  const [layoutOrder, setLayoutOrder] = useState(null); // null = still loading; [] = builtin already loaded
  useEffect(() => {
    if (!activeVenue?.venue_id) return;
    let cancelled = false;
    axios.get(`${API}/venues/${activeVenue.venue_id}/layout?layout_type=owner`)
      .then((r) => { if (!cancelled) setLayoutOrder(r.data?.sections || []); })
      .catch(() => { if (!cancelled) setLayoutOrder([]); });
    return () => { cancelled = true; };
  }, [activeVenue?.venue_id]);

  // Map nav-item path → layout key so we can sort + filter against the
  // resolved layout. Stable, declared once.
  const PATH_TO_LAYOUT_KEY = {
    "/owner": "overview",
    "/owner/venue": "venue",
    "/owner/gallery": "gallery",
    "/owner/availability": "availability",
    "/owner/menu": "menu",
    "/owner/promotions": "promotions",
    "/owner/bookings": "bookings",
    "/owner/calendar": "calendar",
    "/owner/reviews": "reviews",
    "/owner/schedule": "schedule",
    "/owner/preview": "preview",
    "/owner/billing": "billing",
    "/owner/team": "team",
    "/owner/settings": "settings",
  };

  // Compute a `locked` flag per item but keep them all visible. Then apply
  // the layout-resolved order + visibility on top.
  const enrichedItems = allNavItems
    .filter((item) => bookingsEnabled || !item.bookingsGated)
    .map((item) => ({
      ...item,
      locked: item.requires ? !featureCan(item.requires) : false,
      layoutKey: PATH_TO_LAYOUT_KEY[item.path],
    }));

  // Locked items are hidden entirely per the user spec.
  let navItems = enrichedItems.filter((item) => !item.locked);

  if (Array.isArray(layoutOrder) && layoutOrder.length > 0) {
    const order = new Map();
    let hiddenKeys = new Set();
    layoutOrder.forEach((s, i) => {
      order.set(s.key, i);
      if (s.visible === false) hiddenKeys.add(s.key);
    });
    navItems = navItems
      .filter((item) => !item.layoutKey || !hiddenKeys.has(item.layoutKey))
      .sort((a, b) => {
        const ai = order.has(a.layoutKey) ? order.get(a.layoutKey) : 999;
        const bi = order.has(b.layoutKey) ? order.get(b.layoutKey) : 999;
        return ai - bi;
      });
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <Header />

      <div className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Sidebar */}
            <aside className="lg:w-64 shrink-0">
              <div className="bg-card rounded-xl border border-border p-4 mb-4" data-testid="owner-venue-card">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center shrink-0">
                    <Building2 className="h-5 w-5 text-primary" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-foreground text-sm truncate" data-testid="owner-active-venue-name">
                      {activeVenueName || t("owner:venue_dashboard", { defaultValue: "Venue Dashboard" })}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">{user?.name}</p>
                  </div>
                </div>
                {venues.length > 1 && (
                  <div className="mt-3">
                    <Select value={activeVenueId} onValueChange={handleVenueChange}>
                      <SelectTrigger className="h-8 text-xs" data-testid="owner-venue-switcher">
                        <SelectValue placeholder={t("owner:switch_venue", { defaultValue: "Switch venue" })} />
                      </SelectTrigger>
                      <SelectContent>
                        {venues.map((v) => (
                          <SelectItem key={v.venue_id} value={v.venue_id}>
                            {(lang === "el" && v.name_el) || v.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              <nav className="space-y-1">
                {navItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-muted hover:text-foreground"
                      }`}
                      data-testid={`owner-nav-${item.label.toLowerCase().replace(" ", "-")}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.label}
                    </Link>
                  );
                })}
              </nav>
            </aside>

            {/* Main Content */}
            <main className="flex-1 min-w-0">
              {!bookingsEnabled && (
                <div
                  className="mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4"
                  data-testid="owner-bookings-disabled-banner"
                >
                  <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">
                      {t("owner:bookings_disabled_title", { defaultValue: "Bookings are currently disabled for this venue" })}
                    </p>
                    <p className="text-xs text-amber-800 mt-1">
                      {t("owner:bookings_disabled_desc", { defaultValue: "Contact an administrator to re-enable bookings. Sections related to reservations are hidden in the meantime." })}
                    </p>
                  </div>
                </div>
              )}
              <Routes>
                <Route index element={
                  bookingsEnabled
                    ? (featureCan("owner_dashboard") ? <OverviewPage /> : <UpgradePrompt feature={t("owner:overview", { defaultValue: "Overview" })} planName={planName} />)
                    : <Navigate to="/owner/venue" replace />
                } />
                <Route path="bookings" element={
                  bookingsEnabled && featureCan("bookings_enabled")
                    ? <OwnerBookingsPage />
                    : <UpgradePrompt feature={t("owner:bookings")} planName={planName} />
                } />
                <Route path="calendar" element={
                  bookingsEnabled && featureCan("calendar_view")
                    ? <OwnerCalendar />
                    : <UpgradePrompt feature={t("owner:calendar")} planName={planName} />
                } />
                <Route path="reviews" element={
                  // Reviews UI uses the new `can_*` toggles; fall back to the
                  // legacy `reviews_enabled`/`review_responses` so older
                  // plans still work. Render the OwnerReviewsPage if the
                  // plan can do anything review-related (receive, reply, or
                  // analytics) — the page itself shows the analytics widget
                  // only when `can_access_review_analytics` is also on.
                  (featureCan("can_receive_reviews") || featureCan("can_reply_to_reviews") || featureCan("can_access_review_analytics")
                    || featureCan("reviews_enabled") || featureCan("review_responses"))
                    ? <OwnerReviewsPage />
                    : <UpgradePrompt feature={t("owner:reviews")} planName={planName} />
                } />
                <Route path="venue" element={<OwnerVenuePage />} />
                <Route path="gallery" element={
                  featureCan("photos_extended")
                    ? <OwnerGalleryPage />
                    : <UpgradePrompt feature={t("owner:gallery")} planName={planName} />
                } />
                <Route path="availability" element={
                  featureCan("availability_overrides")
                    ? <OwnerAvailability />
                    : <UpgradePrompt feature={t("owner:availability")} planName={planName} />
                } />
                <Route path="schedule" element={bookingsEnabled ? <OwnerScheduling /> : <Navigate to="/owner/venue" replace />} />
                <Route path="menu" element={
                  featureCan("menu_module")
                    ? <OwnerMenu />
                    : <UpgradePrompt feature={t("owner:menu")} planName={planName} />
                } />
                <Route path="promotions" element={
                  featureCan("news_posts")
                    ? <OwnerPromotions />
                    : <UpgradePrompt feature={t("owner:offers", { defaultValue: "Offers" })} planName={planName} />
                } />
                <Route path="scorecard" element={<Navigate to={bookingsEnabled ? "/owner" : "/owner/venue"} replace />} />
                <Route path="preview" element={<VenuePreviewPage />} />
                <Route path="billing" element={<OwnerBillingPage />} />
                <Route path="team" element={bookingsEnabled ? <OwnerTeamPage /> : <Navigate to="/owner/venue" replace />} />
                <Route path="settings" element={<OwnerSettingsPage />} />
              </Routes>
            </main>
          </div>
        </div>
      </div>
    </div>
  );
}


/* ============== UPGRADE PROMPT (Phase 3) ============== */
/** Inline page rendered when an owner navigates to a feature their plan
 *  doesn't include. Pairs with a toast triggered from the sidebar click. */
function UpgradePrompt({ feature, planName }) {
  const { t } = useTranslation();
  return (
    <div className="bg-card rounded-2xl border border-border p-10 text-center max-w-2xl mx-auto" data-testid="owner-upgrade-prompt">
      <div className="w-16 h-16 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <Lock className="h-8 w-8 text-primary" />
      </div>
      <h2 className="text-2xl font-medium text-foreground mb-2">
        {t("owner:upgrade_title", { defaultValue: "{{feature}} is locked", feature })}
      </h2>
      <p className="text-muted-foreground mb-1">
        {t("owner:upgrade_subtitle", {
          defaultValue: "Your venue is on the {{plan}} plan. Upgrade to unlock this feature.",
          plan: planName || "current",
        })}
      </p>
      <p className="text-sm text-muted-foreground mb-6">
        {t("owner:upgrade_contact", {
          defaultValue: "Pricing is handled directly with our team — get in touch to upgrade.",
        })}
      </p>
      <div className="flex items-center justify-center gap-2">
        <Button asChild>
          <Link to="/owner/billing" data-testid="upgrade-billing-link">
            {t("owner:upgrade_view_billing", { defaultValue: "View billing" })}
          </Link>
        </Button>
        <Button asChild variant="outline">
          <a href="mailto:hello@tablecy.com?subject=Plan%20upgrade%20enquiry" data-testid="upgrade-contact-link">
            {t("owner:upgrade_contact_btn", { defaultValue: "Contact sales" })}
          </a>
        </Button>
      </div>
    </div>
  );
}
